/**
 * The notification settings of the one settings list (I-2915) — the rows
 * that are only known at runtime.
 *
 * Which notifications exist is no list of the shell's: every module declares
 * its notification types to master, and master serves the register
 * (`GET /v1/notifications/types`). So the rows are declared from it when the
 * shell collects the declarations (`settingsCatalog.ts`): for every module
 * that has types a group of the section „Benachrichtigungen" with one row per device type
 * („Web", „Desktop", „Mobil") for the module as a whole, and — rarely needed,
 * under „Erweitert" — one row per type and device type. Each is a choice
 * „Aus" · „Nur Glocke" · „Glocke + Systemhinweis"; a type's row has „Wie
 * Modul" first (no value of its own). They are ordinary declared fields of
 * the shell's key — the same list, search, layers („Bearbeiten für"), locks
 * and designs as every other setting:
 *
 *   shell → notifications.devices.<device type>.modules.<module key>             default: defaultNotificationSetting
 *   shell → notifications.devices.<device type>.types.<module key>.<type name>   default: „Wie Modul"; off for a type that is off by default
 *   shell → notifications.default_levels.<module key>.<context type>              default: all
 *
 * The last is the default notification level (I-3826) of a module's
 * contexts of one type — a chat, a mailbox —, one row per context type its
 * types name: „Alle" · „Nur Erwähnungen" · „Aus". A context's own level and a
 * mute are set at the context itself (the app); master computes what applies.
 *
 * Master shows every session what these settings show for the session's own
 * scope — its company, its appearance, its account and the profile it works
 * in — on its device type: the list, the unread count, the long poll and the
 * change feed (`backend/master/notifications/filter.go`). So the rows take
 * effect on whatever layer the list edits, a profile's included. The rule
 * and the defaults are `notificationDeviceSettings.ts`, the client's copy of
 * master's.
 *
 * Their texts are not keys of Connect's table: a type's title and description
 * are keys of the app that declares it (`titleKey`, `descriptionKey` in the
 * table whose id is the module key), with Connect's own table and then the
 * register's English text as the fallbacks. The rows carry reference keys
 * (`@notifications/…`) that `notificationSettingsTranslator` resolves when a
 * text is read, so they follow the language like every other row.
 */
import type {
  ConnectSettingsField,
  ConnectSettingsGroup,
  ConnectSettingsOption,
  ConnectSettingsSection,
} from '@blpde/connect';
import type { Translator } from '@/i18n/appI18n';
import {
  defaultNotificationSetting,
  NOTIFICATION_DEVICE_SETTINGS,
  NOTIFICATION_DEVICE_TYPES,
  type NotificationDeviceType,
} from '@/notificationDeviceSettings';
import { MASTER_MODULE_KEY, type NotificationType } from '@/notifications';
import { NOTIFICATIONS_SECTION_ID, SHELL_CONFIG_KEY } from './connectSettingsSections';

const T = 'connect.settingsList';
/** The head of a reference key: no i18n key starts like this. */
const REF = '@notifications/';

/** A name master accepts as a module key or a type name (lower snake case). */
const NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

/** A module of the register as the settings name it. */
export interface NotificationSettingsModule {
  /** The module key (`mail`); master's own types sit under `master`. */
  key: string;
  /** The app of that key, when Connect hosts one: its texts are the types'. */
  app?: string;
  /** i18n key of the module's name in Connect's own table. */
  labelKey?: string;
}

/** What the builder needs to know about the apps: which exist, which the account may use. */
export interface NotificationSettingsApps {
  /** The app whose key is the module key; undefined when Connect hosts none. */
  find(moduleKey: string): { id: string; labelKey: string } | undefined;
  /** The account may use the app. */
  entitled(appId: string): boolean;
  /** The apps' keys in the shell's order (the order of the groups). */
  order: readonly string[];
}

interface TypeRow {
  module: string;
  name: string;
  type: NotificationType;
}

/** A register entry as a settings row; null for a key that is no `<module>.<name>`. */
function typeRow(type: NotificationType): TypeRow | null {
  const module = type.moduleKey || MASTER_MODULE_KEY;
  const prefix = `${module}.`;
  if (!type.key?.startsWith(prefix)) return null;
  const name = type.key.slice(prefix.length);
  if (!NAME_PATTERN.test(module) || !NAME_PATTERN.test(name)) return null;
  return { module, name, type };
}

/**
 * The register's types the account sees, by module: master's own first, then
 * the apps in the shell's order, then modules without an app by key. Types of
 * an app the account may not use are left out. Inside a module the register's
 * order (`sortOrder`, key) holds.
 */
export function notificationSettingsModules(
  types: readonly NotificationType[],
  apps: NotificationSettingsApps
): { module: string; rows: TypeRow[] }[] {
  const byModule = new Map<string, TypeRow[]>();
  for (const type of types) {
    const row = typeRow(type);
    if (!row) continue;
    const app = apps.find(row.module);
    if (app && !apps.entitled(app.id)) continue;
    byModule.set(row.module, [...(byModule.get(row.module) ?? []), row]);
  }
  const rank = (module: string): number => {
    if (module === MASTER_MODULE_KEY) return -1;
    const at = apps.order.indexOf(module);
    return at < 0 ? apps.order.length : at;
  };
  return [...byModule.entries()]
    .map(([module, rows]) => ({
      module,
      rows: [...rows].sort((a, b) => a.type.sortOrder - b.type.sortOrder || a.type.key.localeCompare(b.type.key)),
    }))
    .sort((a, b) => rank(a.module) - rank(b.module) || a.module.localeCompare(b.module));
}

/** The id of a module's group inside the section „Benachrichtigungen". */
export function notificationSettingsGroupId(module: string): string {
  return `${NOTIFICATIONS_SECTION_ID}.${module}`;
}

/** „Aus" · „Nur Glocke" · „Glocke + Systemhinweis". */
const SETTING_OPTIONS: readonly ConnectSettingsOption[] = NOTIFICATION_DEVICE_SETTINGS.map((value) => ({
  value,
  labelKey: `${T}.fields.notificationSetting.options.${value}`,
}));

/** A type's row: „Wie Modul" (no value of its own) first. */
const TYPE_SETTING_OPTIONS: readonly ConnectSettingsOption[] = [
  { value: null, labelKey: `${T}.fields.notificationSetting.options.asModule` },
  ...SETTING_OPTIONS,
];

/** „Alle" · „Nur Erwähnungen" · „Aus": the notification levels (I-3826). */
const LEVEL_OPTIONS: readonly ConnectSettingsOption[] = (['all', 'mentions', 'off'] as const).map((value) => ({
  value,
  labelKey: `${T}.fields.notificationLevel.options.${value}`,
}));

/** Search words of a device type's rows, in both languages. */
const DEVICE_KEYWORDS: Record<NotificationDeviceType, readonly string[]> = {
  browser: ['Web', 'Browser', 'Systemhinweis', 'System notification'],
  desktop: ['Desktop', 'App', 'Systemhinweis', 'System notification'],
  mobile: ['Mobil', 'Mobile', 'Handy', 'Smartphone', 'Push'],
};

/**
 * The groups and fields the register adds to the section „Benachrichtigungen": per module
 * a group with one row per device type for the module, and one row per type
 * and device type under „Erweitert".
 */
export function notificationSettingsRows(
  types: readonly NotificationType[],
  apps: NotificationSettingsApps
): { groups: ConnectSettingsGroup[]; fields: ConnectSettingsField[] } {
  const groups: ConnectSettingsGroup[] = [];
  const fields: ConnectSettingsField[] = [];
  for (const { module, rows } of notificationSettingsModules(types, apps)) {
    const group = notificationSettingsGroupId(module);
    groups.push({ id: group, labelKey: `${REF}module/${module}/-/group` });
    for (const device of NOTIFICATION_DEVICE_TYPES) {
      fields.push({
        key: SHELL_CONFIG_KEY,
        path: ['notifications', 'devices', device, 'modules', module],
        control: 'choice',
        options: SETTING_OPTIONS,
        default: defaultNotificationSetting(device, module),
        group,
        labelKey: `${REF}module/${module}/${device}/label`,
        descriptionKey: `${REF}module/${module}/${device}/description`,
        keywords: [module, ...DEVICE_KEYWORDS[device]],
      });
    }
    // The default level of the module's contexts, per context type its
    // types name (I-3826).
    const contextTypes = [...new Set(rows.map((row) => row.type.contextType).filter((c) => NAME_PATTERN.test(c ?? '')))];
    for (const contextType of contextTypes.sort()) {
      fields.push({
        key: SHELL_CONFIG_KEY,
        path: ['notifications', 'default_levels', module, contextType],
        control: 'choice',
        options: LEVEL_OPTIONS,
        default: 'all',
        group,
        labelKey: `${REF}level/${module}/${contextType}/label`,
        descriptionKey: `${REF}level/${module}/${contextType}/description`,
        keywords: [module, contextType, 'Erwähnung', 'Stumm', 'Mention', 'Mute'],
      });
    }
    for (const { name, type } of rows) {
      // A type that is off by default stays off without a setting of its
      // own: „Wie Modul" would promise what it does not do.
      const onByDefault = type.enabledByDefault !== false;
      for (const device of NOTIFICATION_DEVICE_TYPES) {
        fields.push({
          key: SHELL_CONFIG_KEY,
          path: ['notifications', 'devices', device, 'types', module, name],
          control: 'choice',
          options: onByDefault ? TYPE_SETTING_OPTIONS : SETTING_OPTIONS,
          default: onByDefault ? null : 'off',
          group,
          advanced: true,
          labelKey: `${REF}type/${type.key}/${device}/label`,
          descriptionKey: `${REF}type/${type.key}/${device}/description`,
          keywords: [type.key, type.label, ...DEVICE_KEYWORDS[device]].filter(Boolean),
        });
      }
    }
  }
  return { groups, fields };
}

/**
 * Connect's own sections with the register's rows in the section „Benachrichtigungen":
 * its groups and fields after the declared ones. Without types the sections
 * are returned as they are.
 */
export function withNotificationSettings(
  sections: readonly ConnectSettingsSection[],
  types: readonly NotificationType[],
  apps: NotificationSettingsApps
): ConnectSettingsSection[] {
  const { groups, fields } = notificationSettingsRows(types, apps);
  if (!fields.length) return [...sections];
  return sections.map((section) =>
    section.id === NOTIFICATIONS_SECTION_ID
      ? {
          ...section,
          groups: [...(section.groups ?? []), ...groups],
          fields: [...section.fields, ...fields],
        }
      : section
  );
}

/** What the translator needs: Connect's own table and every app's. */
export interface NotificationSettingsTexts {
  /** Connect's own table. */
  shell: Translator;
  /** The table of an app (its key is the module key); undefined when Connect hosts none. */
  app(moduleKey: string): Translator | undefined;
  /** i18n key of an app's name in Connect's table; undefined when Connect hosts none. */
  appLabelKey(moduleKey: string): string | undefined;
}

function isDeviceType(value: string): value is NotificationDeviceType {
  return (NOTIFICATION_DEVICE_TYPES as readonly string[]).includes(value);
}

/**
 * `t` for a declaration source that holds the register's rows: a reference
 * key (`@notifications/<kind>/<id>/<device type>/<part>`) is resolved against
 * the register, every other key is Connect's own.
 */
export function notificationSettingsTranslator(
  types: readonly NotificationType[],
  texts: NotificationSettingsTexts
): Translator['t'] {
  const byKey = new Map(types.map((type) => [type.key, type]));

  /** The module's name: the app's, „Allgemein" for master's own, else its key. */
  function moduleName(module: string): string {
    if (module === MASTER_MODULE_KEY) return texts.shell.t(`${T}.notificationModules.master`);
    const labelKey = texts.appLabelKey(module);
    if (labelKey && texts.shell.te(labelKey)) return texts.shell.t(labelKey);
    return module.toUpperCase();
  }

  /** A text of a type: its key in the declaring app's table, then in Connect's; empty without one. */
  function typeText(type: NotificationType, key: string): string {
    if (!key) return '';
    const app = texts.app(type.moduleKey);
    if (app?.te(key)) return app.t(key);
    return texts.shell.te(key) ? texts.shell.t(key) : '';
  }

  function typeName(type: NotificationType): string {
    return typeText(type, type.titleKey) || type.label || type.key;
  }

  return (key, named, plural) => {
    if (!key.startsWith(REF)) return texts.shell.t(key, named, plural);
    const [kind, id, device, part] = key.slice(REF.length).split('/');
    if (kind === 'level') {
      // id is the module, device the context type; its name is the app's
      // `notifications.contexts.<context type>`, else the type itself.
      const contextKey = `notifications.contexts.${device}`;
      const app = texts.app(id);
      const context = app?.te(contextKey) ? app.t(contextKey) : device;
      return texts.shell.t(`${T}.fields.notificationLevel.${part === 'label' ? 'label' : 'description'}`, {
        context,
        module: moduleName(id),
      });
    }
    if (kind === 'module') {
      const module = moduleName(id);
      if (part === 'group') return texts.shell.t(`${T}.groups.notificationsModule`, { module });
      if (!isDeviceType(device)) return key;
      if (part === 'label') return texts.shell.t(`${T}.notificationDeviceTypes.${device}`);
      return texts.shell.t(`${T}.fields.notificationsModule.description.${device}`, { module });
    }
    const type = byKey.get(id);
    if (!type || !isDeviceType(device)) return id;
    const deviceName = texts.shell.t(`${T}.notificationDeviceTypes.${device}`);
    if (part === 'label') return texts.shell.t(`${T}.fields.notificationType.label`, { type: typeName(type), device: deviceName });
    const about =
      typeText(type, type.descriptionKey) ||
      type.description ||
      texts.shell.t(`${T}.fields.notificationType.about`, { type: typeName(type) });
    const rule = type.enabledByDefault === false ? 'offByDefault' : 'asModule';
    return texts.shell.t(`${T}.fields.notificationType.description.${rule}.${device}`, {
      about,
      module: moduleName(type.moduleKey || MASTER_MODULE_KEY),
    });
  };
}
